import { z } from 'zod';
import { reportingBuiltInDefinitionKeySchema } from './reporting-finance-project-performance.contract.js';
export const reportingSchedulesCapability = 'reporting.schedules.v1';
export const reportingScheduleRunsCapability = 'reporting.schedule-runs.v1';
const isIanaTimeZone = (value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
    }
    catch {
        return false;
    }
};
export const reportingScheduleCadenceSchema = z.enum(['daily', 'weekly', 'monthly']);
export const reportingScheduleStatusSchema = z.enum(['active', 'paused', 'archived']);
export const reportingScheduleOutcomeCodeSchema = z.string().regex(/^[a-z][a-z0-9-]{0,79}$/);
export const reportingScheduleSourceSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('built-in'), key: reportingBuiltInDefinitionKeySchema }),
    z.strictObject({ kind: z.literal('tenant-definition'), definitionId: z.string().uuid() }),
]);
export const reportingScheduleValueSchema = z.strictObject({
    name: z.string().trim().min(1).max(160),
    source: reportingScheduleSourceSchema,
    cadence: reportingScheduleCadenceSchema,
    timeZone: z.string().trim().min(1).max(100).refine(isIanaTimeZone, 'Expected an IANA time zone.'),
    localTime: z.string().regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/),
    isoDayOfWeek: z.number().int().min(1).max(7).nullable(),
    dayOfMonth: z.number().int().min(1).max(31).nullable(),
    recipientMembershipIds: z.array(z.string().uuid()).min(1).max(50),
    subject: z.string().trim().min(1).max(240),
    body: z.string().trim().min(1).max(10_000),
    format: z.literal('csv'),
}).superRefine((value, context) => {
    if (new Set(value.recipientMembershipIds).size !== value.recipientMembershipIds.length) {
        context.addIssue({
            code: 'custom', path: ['recipientMembershipIds'], message: 'Schedule recipients cannot repeat.',
        });
    }
    const validDayShape = value.cadence === 'daily'
        ? value.isoDayOfWeek === null && value.dayOfMonth === null
        : value.cadence === 'weekly'
            ? value.isoDayOfWeek !== null && value.dayOfMonth === null
            : value.isoDayOfWeek === null && value.dayOfMonth !== null;
    if (!validDayShape) {
        context.addIssue({
            code: 'custom', path: ['cadence'],
            message: 'Cadence requires exactly its matching day selector.',
        });
    }
});
export const reportingScheduleVersionSchema = reportingScheduleValueSchema.extend({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    scheduleId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    sourceVersionId: z.string().uuid().nullable(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(5).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const reportingScheduleSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    status: reportingScheduleStatusSchema,
    revision: z.number().int().positive(),
    latestVersionId: z.string().uuid(),
    latestVersion: reportingScheduleVersionSchema,
    nextRunAt: z.string().datetime({ offset: true }).nullable(),
    lastScheduledFor: z.string().datetime({ offset: true }).nullable(),
    lastOutcomeCode: reportingScheduleOutcomeCodeSchema.nullable(),
    runAsMembershipId: z.string().uuid(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if ((value.status === 'active') !== (value.nextRunAt !== null)) {
        context.addIssue({ code: 'custom', path: ['nextRunAt'], message: 'Active schedules require a next run.' });
    }
    if ((value.status === 'archived') !== (value.archivedAt !== null)) {
        context.addIssue({ code: 'custom', path: ['archivedAt'], message: 'Archive state is inconsistent.' });
    }
});
export const reportingScheduleListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const reportingCreateScheduleSchema = z.strictObject({
    commandId: z.string().uuid(),
    schedule: reportingScheduleValueSchema,
    reason: z.string().trim().min(5).max(500),
});
export const reportingAppendScheduleVersionSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    schedule: reportingScheduleValueSchema,
    reason: z.string().trim().min(5).max(500),
});
export const reportingScheduleLifecycleCommandSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(500),
});
export const reportingRollbackScheduleSchema = reportingScheduleLifecycleCommandSchema.extend({
    sourceVersionId: z.string().uuid(),
});
const reportingScheduleCommandMetadataSchema = z.strictObject({
    commandId: z.string().uuid(),
    idempotent: z.boolean(),
});
export const reportingScheduleResponseSchema = z.strictObject({
    data: reportingScheduleSchema,
    command: reportingScheduleCommandMetadataSchema.optional(),
});
export const reportingSchedulesResponseSchema = z.strictObject({
    data: z.array(reportingScheduleSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const reportingScheduleVersionsResponseSchema = z.strictObject({
    data: z.array(reportingScheduleVersionSchema),
});
export const reportingScheduleRunSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), scheduleId: z.string().uuid(),
    scheduleVersionId: z.string().uuid(), definitionVersionId: z.string().uuid().nullable(),
    executionId: z.string().uuid(), scheduledFor: z.string().datetime({ offset: true }),
    state: z.enum(['pending', 'prepared', 'completed', 'suppressed', 'failed']),
    attemptCount: z.number().int().nonnegative().max(10),
    outcomeCode: reportingScheduleOutcomeCodeSchema.nullable(),
    outcomeDetails: z.record(z.string(), z.unknown()),
    artifact: z.strictObject({
        id: z.string().uuid(), fileName: z.string().trim().min(1).max(180),
        contentType: z.literal('text/csv; charset=utf-8'),
        sha256: z.string().regex(/^[a-f0-9]{64}$/), byteLength: z.number().int().positive(),
        rowCount: z.number().int().nonnegative().max(500), truncated: z.boolean(),
        retentionDays: z.number().int().min(1).max(30),
        expiresAt: z.string().datetime({ offset: true }),
        purgedAt: z.string().datetime({ offset: true }).nullable(),
        createdAt: z.string().datetime({ offset: true }),
    }).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const reportingScheduleRunsResponseSchema = z.strictObject({
    data: z.array(reportingScheduleRunSchema),
});
//# sourceMappingURL=reporting-schedule.contract.js.map